import { usesEducationalStore } from '@bloom/shared';
import { dtpPath, useDtpParams } from '../../../lib/clinicSite';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { dtpApi } from '../../../lib/dtpApi';
import { useDtpCartStore } from '../../../store/useDtpCartStore';
import type { DtpCategoryContentResponse, DtpProductResponse } from '@bloom/shared';
import type { ClinicInfo } from './EducationalShell';

export interface EducationalCatalog {
  intakePrograms: import("@bloom/shared").PublicIntakeProgram[];
  loading: boolean;
  error: string;
  clinic: ClinicInfo | null;
  products: DtpProductResponse[];
  categoryContent: DtpCategoryContentResponse[];
  physicianId: string;
}

export function useEducationalCatalog(): EducationalCatalog {
  const { physicianId } = useDtpParams<{ physicianId: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const applyClinicFromCatalog = useDtpCartStore(s => s.applyClinicFromCatalog);

  // Seed from the shared catalog cache so moving between store pages renders
  // immediately instead of flashing a spinner.
  const [cached] = useState(() => (physicianId ? dtpApi.peekCatalog(physicianId)?.data : undefined));
  const [intakePrograms, setIntakePrograms] = useState<import("@bloom/shared").PublicIntakeProgram[]>(cached?.intakePrograms || []);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState('');
  const [clinic, setClinic] = useState<ClinicInfo | null>((cached?.clinic as ClinicInfo) || null);
  const [products, setProducts] = useState<DtpProductResponse[]>(cached?.products || []);
  const [categoryContent, setCategoryContent] = useState<DtpCategoryContentResponse[]>(cached?.categoryContent || []);

  useEffect(() => {
    if (!physicianId) return;
    if (!dtpApi.peekCatalog(physicianId)) setLoading(true);
    dtpApi.getCatalog(physicianId)
      .then((res: any) => {
        if (!res.success || !res.data) {
          setError('Clinic not found.');
          return;
        }
        const data = res.data;
        if (!usesEducationalStore(data.clinic) && /\/learn(?:\/|$)/.test(pathname)) {
          navigate(dtpPath(physicianId, `/shop`), { replace: true });
          return;
        }
        setClinic(data.clinic);
        setIntakePrograms(data.intakePrograms || []);
        setProducts(data.products || []);
        setCategoryContent(data.categoryContent || []);
        applyClinicFromCatalog(data.clinic);
      })
      .catch(() => setError('Failed to load store.'))
      .finally(() => setLoading(false));
  }, [physicianId, navigate, applyClinicFromCatalog, pathname]);

  return { intakePrograms, loading, error, clinic, products, categoryContent, physicianId: physicianId || '' };
}

/**
 * Best-effort family display name. Uses curated overrides for slugs that don't
 * title-case cleanly, otherwise falls back to the first product's drugName.
 */
const FAMILY_NAME_OVERRIDES: Record<string, string> = {
  'oral-ed': 'Oral ED therapies',
  'lipotropic-injections': 'Lipotropic blends',
  'female-arousal-topicals': 'Arousal topicals',
  'pt141': 'PT-141',
  'tb500': 'TB-500',
  'cjc1295': 'CJC-1295',
  'mots-c': 'MOTS-c',
  'ghk-cu': 'GHK-Cu',
  'bpc157': 'BPC-157',
  'nad': 'NAD+',
  'nad-topical': 'NAD topical',
  'hair-regrowth': 'Hair regrowth',
  'daily-vitamins': 'Daily vitamins',
};

export function getFamilyDisplayName(slug: string, products: { drugName?: string; drugFamilySlug?: string }[]): string {
  if (FAMILY_NAME_OVERRIDES[slug]) return FAMILY_NAME_OVERRIDES[slug];
  const first = products.find(p => p.drugFamilySlug === slug);
  return first?.drugName || slug;
}
